import React, { useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, Paper, IconButton, CircularProgress,
  Switch, Chip, Divider, InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import { useCreateCompanyMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { useNavigate } from "react-router";

const labelSx = { fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 };
const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 } } };

const Field = ({ label, required, ...props }: { label: string; required?: boolean; [k: string]: any }) => (
  <Box>
    <Typography sx={{ ...labelSx, display: "flex", alignItems: "center", gap: 0.5 }}>
      {label} {required && <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>}
    </Typography>
    <TextField fullWidth size="small" {...props} sx={{ ...inputSx, ...props.sx }} />
  </Box>
);

// Format phone: (xxx) xxx-xxxx
const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const CompanyCreate: React.FC = () => {
  const navigate = useNavigate();
  const [newCompany, { isLoading }] = useCreateCompanyMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedLogo, setSelectedLogo] = useState<File | undefined>(undefined);
  const [selectedWatermark, setSelectedWatermark] = useState<File | undefined>(undefined);
  const [addressSearch, setAddressSearch] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressRef = React.useRef<HTMLDivElement>(null);

  // Click outside to close suggestions
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addressRef.current && !addressRef.current.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Address autocomplete using OpenStreetMap Nominatim (free, no key needed)
  React.useEffect(() => {
    if (addressSearch.length < 3) { setAddressSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=ca,us&limit=5&q=${encodeURIComponent(addressSearch)}`);
        const data = await res.json();
        setAddressSuggestions(data || []);
        setShowSuggestions(true);
      } catch { setAddressSuggestions([]); }
    }, 400);
    return () => clearTimeout(timer);
  }, [addressSearch]);

  const handleAddressSelect = (place: any) => {
    const addr = place.address || {};
    formik.setFieldValue("companyStreetNumber", addr.house_number || "");
    formik.setFieldValue("companyStreetName", addr.road || "");
    formik.setFieldValue("companyCity", addr.city || addr.town || addr.village || addr.municipality || "");
    formik.setFieldValue("companyProvince", addr.state || addr.province || "");
    formik.setFieldValue("companyPostalCode", addr.postcode || "");
    setAddressSearch(place.display_name || "");
    setShowSuggestions(false);
  };

  const handlePhoneChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field, formatPhone(e.target.value));
  };

  const formik = useFormik({
    initialValues: {
      companyName: "", companySlog: "", companyProvince: "", companyCity: "",
      companyPostalCode: "", companyStreetNumber: "", companyStreetName: "",
      companyEmail: "", companyMobileNumber: "", companyAltMobileNumber: "",
      companyWebSiteLink: "", companyHstNo: "", companyDescription: "",
      companyFooterDetails: "", isDefault: false,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.companyName.trim()) errors.companyName = "Company name is required";
      if (values.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.companyEmail)) errors.companyEmail = "Invalid email";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let CompanyLogo: string | undefined;
        let CompanyWatermark: string | undefined;
        if (selectedLogo) CompanyLogo = (await uploadImage(selectedLogo)) || "";
        if (selectedWatermark) CompanyWatermark = (await uploadImage(selectedWatermark)) || "";

        const res = await newCompany({ ...values, CompanyLogo, CompanyWatermark }).unwrap();
        if (!res.status) showErrorMessage(res.message);
        else { showMessage("Company created successfully"); resetForm(); setSelectedLogo(undefined); setSelectedWatermark(undefined); navigate("/company/list"); }
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  const formValid = formik.values.companyName.trim() !== "";

  const handleImageChange = (setter: (f: File | undefined) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setter(file);
  };

  const ImageUpload = ({ id, file, setter, label }: { id: string; file?: File; setter: (f: File | undefined) => void; label: string }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 64, height: 64, borderRadius: 2.5, bgcolor: "#f8fafc", border: file ? "none" : "2px dashed #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
        {file ? (
          <img src={URL.createObjectURL(file)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <BusinessIcon sx={{ fontSize: 24, color: "#cbd5e1" }} />
        )}
      </Box>
      <Box>
        <Typography fontSize={13} fontWeight={600} color="#334155" mb={0.5}>{label}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <input accept="image/*" style={{ display: "none" }} id={id} type="file" onChange={handleImageChange(setter)} />
          <label htmlFor={id}>
            <Button component="span" size="small" variant="outlined" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>
              {file ? "Change" : "Upload"}
            </Button>
          </label>
          {file && (
            <IconButton size="small" onClick={() => setter(undefined)} sx={{ width: 28, height: 28, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
              <DeleteOutlineIcon sx={{ fontSize: 14, color: "#ef4444" }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f8f9fb", minHeight: "100vh" }}>
      {/* Top Bar */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2, position: "sticky", top: 0, zIndex: 10 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 1440, mx: "auto" }}>
          <IconButton onClick={() => navigate("/company/list")} size="small" sx={{ border: "1px solid #e2e8f0", borderRadius: 2, width: 36, height: 36 }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <Typography fontSize={18} fontWeight={700} color="#0f172a" sx={{ flex: 1 }}>New Company</Typography>
          <Button variant="outlined" size="small" onClick={() => navigate("/company/list")}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b", mr: 1 }}>Cancel</Button>
          <Button variant="contained" disableElevation size="small" disabled={!formValid || isLoading} onClick={() => formik.handleSubmit()}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#0f172a", px: 3, "&:hover": { bgcolor: "#1e293b" }, "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
            {isLoading ? "Saving..." : "Create Company"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1440, mx: "auto", p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* LEFT */}
            <Grid item xs={12} lg={8}>
              {/* Company Details */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Company Details</Typography>
                  <Chip label="Required" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", ml: 0.5 }} />
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Field label="Company Name" required placeholder="Enter company name"
                      {...formik.getFieldProps("companyName")}
                      error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                      helperText={formik.touched.companyName && formik.errors.companyName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Slogan / Tagline" placeholder="Your company slogan" {...formik.getFieldProps("companySlog")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Website" placeholder="https://example.com" {...formik.getFieldProps("companyWebSiteLink")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="HST / Tax Number" placeholder="HST number" {...formik.getFieldProps("companyHstNo")} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Switch size="small" checked={formik.values.isDefault as boolean} onChange={(_, v) => formik.setFieldValue("isDefault", v)} />
                      <StarIcon sx={{ fontSize: 16, color: formik.values.isDefault ? "#f59e0b" : "#e2e8f0" }} />
                      <Typography fontSize={13} fontWeight={600} color={formik.values.isDefault ? "#f59e0b" : "#94a3b8"}>Set as default company</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Contact */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Contact Information</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Field label="Email" placeholder="company@example.com" type="email"
                      {...formik.getFieldProps("companyEmail")}
                      error={formik.touched.companyEmail && Boolean(formik.errors.companyEmail)}
                      helperText={formik.touched.companyEmail && formik.errors.companyEmail} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field label="Phone" placeholder="(555) 000-0000"
                      value={formik.values.companyMobileNumber}
                      onChange={handlePhoneChange("companyMobileNumber")}
                      onBlur={formik.handleBlur} name="companyMobileNumber" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field label="Alt Phone" placeholder="(555) 000-0000"
                      value={formik.values.companyAltMobileNumber}
                      onChange={handlePhoneChange("companyAltMobileNumber")}
                      onBlur={formik.handleBlur} name="companyAltMobileNumber" />
                  </Grid>
                </Grid>
              </Paper>

              {/* Address */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <LocationOnIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Address</Typography>
                </Box>

                {/* Address Search */}
                <Box ref={addressRef} sx={{ position: "relative", mb: 2.5 }}>
                  <Typography sx={labelSx}>Search Address</Typography>
                  <TextField fullWidth size="small" placeholder="Start typing an address..."
                    value={addressSearch} onChange={e => setAddressSearch(e.target.value)}
                    onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment> }}
                    sx={inputSx} />
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <Paper elevation={3} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, mt: 0.5, borderRadius: 2.5, border: "1px solid #e2e8f0", maxHeight: 220, overflowY: "auto" }}>
                      {addressSuggestions.map((place, i) => (
                        <Box key={i} onClick={() => handleAddressSelect(place)}
                          sx={{ px: 2, py: 1.25, cursor: "pointer", borderBottom: "1px solid #f8fafc", "&:hover": { bgcolor: "#f0fdf4" }, display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <LocationOnIcon sx={{ fontSize: 14, color: "#8b5cf6", mt: 0.25, flexShrink: 0 }} />
                          <Typography fontSize={12} color="#334155" lineHeight={1.4}>{place.display_name}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>

                <Grid container spacing={2.5}>
                  <Grid item xs={4} sm={2}>
                    <Field label="Street #" placeholder="#" {...formik.getFieldProps("companyStreetNumber")} />
                  </Grid>
                  <Grid item xs={8} sm={4}>
                    <Field label="Street Name" placeholder="Main St" {...formik.getFieldProps("companyStreetName")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="City" placeholder="City" {...formik.getFieldProps("companyCity")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Province / State" placeholder="Province" {...formik.getFieldProps("companyProvince")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Postal Code" placeholder="A1B 2C3" {...formik.getFieldProps("companyPostalCode")} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Description & Footer */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <DescriptionIcon sx={{ fontSize: 18, color: "#ea580c" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Additional Details</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Field label="Description" placeholder="About this company..." multiline rows={3} {...formik.getFieldProps("companyDescription")} />
                  </Grid>
                  <Grid item xs={12}>
                    <Field label="Invoice Footer Text" placeholder="Payment terms, thank you notes..." multiline rows={2} {...formik.getFieldProps("companyFooterDetails")} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* RIGHT */}
            <Grid item xs={12} lg={4}>
              {/* Logo */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Typography fontSize={14} fontWeight={700} color="#0f172a" mb={2.5}>Company Logo</Typography>
                <ImageUpload id="company-logo" file={selectedLogo} setter={setSelectedLogo} label="Logo" />
              </Paper>

              {/* Watermark */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Typography fontSize={14} fontWeight={700} color="#0f172a" mb={2.5}>Invoice Watermark</Typography>
                <ImageUpload id="company-watermark" file={selectedWatermark} setter={setSelectedWatermark} label="Watermark" />
              </Paper>

              {/* Save */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Button fullWidth variant="contained" disableElevation disabled={!formValid || isLoading} onClick={() => formik.handleSubmit()}
                  startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                  sx={{ borderRadius: 2, py: 1.5, textTransform: "none", fontWeight: 700, fontSize: 14, bgcolor: "#0f172a",
                    "&:hover": { bgcolor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" }, "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                  {isLoading ? "Saving..." : "Create Company"}
                </Button>
                <Button fullWidth variant="text" size="small" onClick={() => navigate("/company/list")}
                  sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>Cancel</Button>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default CompanyCreate;
